// Public hook + helpers for *property* galleries — per section, per kind.
// Replaces the older `useHotelSectionGalleries` and its cover+images shape.
//
// API:
//   useHotelPropertyGalleries(hotelSlug)
//     → { sections: { [section_slug]: { hero: {images:[]}, gallery: {images:[]} } } }
//
// Helpers:
//   getHeroUrl(sections, section_slug)
//   getGalleryUrls(sections, section_slug)
//   hasAnyHero(sections, section_slug)
//   hasAnyGallery(sections, section_slug)

import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const resolveUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

// Module-level cache so navigating away and back doesn't refetch.
const cache = new Map(); // hotelSlug → { sections }

const EMPTY_SECTIONS = {};

export const useHotelPropertyGalleries = (hotelSlug) => {
  const [sections, setSections] = useState(
    () => cache.get(hotelSlug)?.sections || EMPTY_SECTIONS
  );

  useEffect(() => {
    if (!hotelSlug) return undefined;
    let cancelled = false;
    fetch(`${API}/api/property-galleries/by-hotel/${hotelSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const map = data.sections || {};
        cache.set(hotelSlug, { sections: map });
        setSections(map);
      })
      .catch(() => { /* silent — property galleries are optional */ });
    return () => { cancelled = true; };
  }, [hotelSlug]);

  return sections;
};

// Returns the single hero URL for a section, or null when not set.
export const getHeroUrl = (sections, section_slug) => {
  const list = sections?.[section_slug]?.hero?.images || [];
  if (!list.length) return null;
  return resolveUrl(list[0].url);
};

// Returns the ordered list of gallery URLs for a section.
export const getGalleryUrls = (sections, section_slug) => {
  const list = sections?.[section_slug]?.gallery?.images || [];
  return list.map((img) => resolveUrl(img.url)).filter(Boolean);
};

export const hasAnyHero = (sections, section_slug) =>
  (sections?.[section_slug]?.hero?.images || []).length > 0;

export const hasAnyGallery = (sections, section_slug) =>
  (sections?.[section_slug]?.gallery?.images || []).length > 0;

// Convenience: tile grid (1 panoramic + N thumbs) used by About and
// service-detail pages. Combines hero + gallery into a single visual pool
// when both are present, then pads to `count` by cycling. Defaults to 5
// tiles to preserve the original behaviour for hotel/service pages; pass
// `count: 9` for the expanded experience-page gallery.
export const buildAboutTiles = (sections, section_slug, fallbackPool, count = 5) => {
  const hero = getHeroUrl(sections, section_slug);
  const gallery = getGalleryUrls(sections, section_slug);
  let pool = [];
  if (hero || gallery.length) {
    pool = [hero, ...gallery].filter(Boolean);
  } else {
    pool = (fallbackPool || []).filter(Boolean);
  }
  pool = Array.from(new Set(pool));
  if (pool.length === 0) return null;
  return Array.from({ length: count }, (_, i) => pool[i % pool.length]);
};
